import { compareText } from "@locale-utils";
import type { MimboxData } from "@mimsights-shared/types";

/** Group of the cities without a county. */
export const UNSORTED = "unsorted";

export interface CityOption {
  label: string;
  value: string;
}

export interface CityOptionGroup extends CityOption {
  items: CityOption[];
}

/** City and county names as they're compared and stored in the city filter: trimmed, lowercase. */
export function normalizePlace(name?: string | null): string {
  return name?.trim().toLowerCase() ?? "";
}

function option(value: string): CityOption {
  return { label: value[0].toUpperCase() + value.slice(1), value };
}

/**
 * The county group (`geodata.group_as`) of each city, keyed and valued by normalized name.
 *
 * Boxes in one city can disagree: the county lookup answered in the browser's language, so the
 * same county is stored as "Kalmar län" or "Kalmar" ("Kalmar County" minus "County"). A city goes
 * where most of its boxes say, a county named like the box's own city doesn't count, and a county
 * named like a city that is itself in a county is folded into that county.
 */
export function groupCities(mimboxes: MimboxData[]): Map<string, string> {
  const votes = new Map<string, Map<string, number>>();
  for (const mimbox of mimboxes) {
    const city = normalizePlace(mimbox.install?.geodata?.city);
    if (!city) continue;
    const group = normalizePlace(mimbox.install?.geodata?.group_as);
    if (!group || group === city) continue;
    const cityVotes = votes.get(city) ?? new Map<string, number>();
    cityVotes.set(group, (cityVotes.get(group) ?? 0) + 1);
    votes.set(city, cityVotes);
  }

  const groupOf = new Map<string, string>();
  for (const [city, cityVotes] of votes) {
    const [[group]] = [...cityVotes].sort(([a, na], [b, nb]) => nb - na || compareText(a, b));
    groupOf.set(city, group);
  }

  // "kalmar" → the group of the city Kalmar ("kalmar län"); `seen` stops on cycles
  const resolve = (group: string) => {
    const seen = new Set([group]);
    for (let next = groupOf.get(group); next && !seen.has(next); next = groupOf.get(group)) {
      group = next;
      seen.add(group);
    }
    return group;
  };
  return new Map([...groupOf].map(([city, group]) => [city, resolve(group)]));
}

/**
 * City filter options for `mimboxes`: one option group per county, starting with an option for
 * the whole county, then its cities, and the cities without a county last. Each city is listed
 * once; a city named like a county is selected through that county's option.
 */
export function cityOptions(
  mimboxes: MimboxData[],
  groupOf: Map<string, string>,
): CityOptionGroup[] {
  const groups = new Map<string, Set<string>>();
  for (const mimbox of mimboxes) {
    const city = normalizePlace(mimbox.install?.geodata?.city);
    if (!city) continue;
    const group = groupOf.get(city) ?? UNSORTED;
    if (!groups.has(group)) groups.set(group, new Set());
    groups.get(group)!.add(city);
  }
  const unsorted = groups.get(UNSORTED);
  unsorted?.forEach((city) => groups.has(city) && unsorted.delete(city));
  if (unsorted?.size === 0) groups.delete(UNSORTED);

  return [...groups]
    .map(([group, cities]) => {
      const items = [...cities]
        .filter((city) => city !== group)
        .sort(compareText)
        .map(option);
      if (group !== UNSORTED) items.unshift(option(group));
      return { ...option(group), items };
    })
    .sort((a, b) => {
      if (a.value === UNSORTED) return 1;
      if (b.value === UNSORTED) return -1;
      return compareText(a.value, b.value);
    });
}

/** Whether `mimbox` is in one of the `selected` cities or counties (values from cityOptions). */
export function matchesCityFilter(
  mimbox: MimboxData,
  selected: string[],
  groupOf: Map<string, string>,
): boolean {
  const city = normalizePlace(mimbox.install?.geodata?.city);
  if (!city) return false;
  const group = groupOf.get(city);
  return selected.includes(city) || (!!group && selected.includes(group));
}
